/**
 * SystemHistorie – Admin Audit Log Page
 * Chronological event log with filters, search, and export.
 */
import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../App";
import { toast } from "sonner";
import {
  Shield, Search, Filter, Download, ChevronLeft, ChevronRight,
  Clock, User, Activity, AlertTriangle, AlertOctagon, CheckCircle2,
  Briefcase, ListTodo, Users, Smartphone, Calendar, X, FileText, FileSpreadsheet,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = "/api";

const MODULE_CONFIG = {
  zeiterfassung: { label: "Zeiterfassung", icon: Clock, color: "text-blue-500", bg: "bg-blue-500/10" },
  projekt: { label: "Projekt", icon: Briefcase, color: "text-indigo-500", bg: "bg-indigo-500/10" },
  aufgabe: { label: "Aufgabe", icon: ListTodo, color: "text-emerald-500", bg: "bg-emerald-500/10" },
  benutzer: { label: "Benutzer", icon: Users, color: "text-amber-500", bg: "bg-amber-500/10" },
  crm: { label: "CRM", icon: Smartphone, color: "text-purple-500", bg: "bg-purple-500/10" },
  system: { label: "System", icon: Shield, color: "text-gray-500", bg: "bg-gray-500/10" },
};

const SEVERITY_CONFIG = {
  ok: { label: "OK", icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", border: "border-green-200" },
  warning: { label: "Warnung", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  critical: { label: "Kritisch", icon: AlertOctagon, color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
};

function StatCard({ label, value, icon: Icon, color, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 ${className}`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="w-4 h-4" /></div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function SystemHistorie() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  // Filters
  const [filterUser, setFilterUser] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // User detail view
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userEntries, setUserEntries] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [userDate, setUserDate] = useState(new Date().toISOString().slice(0, 10));

  const loadEntries = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "50" });
    if (filterUser) params.append("user_id", filterUser);
    if (filterModule) params.append("module", filterModule);
    if (filterSeverity) params.append("severity", filterSeverity);
    if (filterDateFrom) params.append("date_from", filterDateFrom);
    if (filterDateTo) params.append("date_to", filterDateTo);
    if (searchText) params.append("search", searchText);

    const res = await authFetch(`${API}/audit-log?${params}`);
    if (res.ok) {
      const data = await res.json();
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
    }
    setLoading(false);
  }, [page, filterUser, filterModule, filterSeverity, filterDateFrom, filterDateTo, searchText]);

  const loadStats = useCallback(async () => {
    const res = await authFetch(`${API}/audit-log/stats`);
    if (res.ok) setStats(await res.json());
  }, []);

  const loadUsers = useCallback(async () => {
    const res = await authFetch(`${API}/users/directory`);
    if (res.ok) setUsers(await res.json());
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadStats(); loadUsers(); }, [loadStats, loadUsers]);

  const loadUserDetail = async (userId, date) => {
    const res = await authFetch(`${API}/audit-log/user/${userId}?date=${date}`);
    if (res.ok) {
      const data = await res.json();
      setUserEntries(data.entries || []);
      setUserInfo(data.user);
    }
  };

  const openUserDetail = (userId) => {
    setSelectedUserId(userId);
    loadUserDetail(userId, userDate);
  };

  const exportLog = async (format) => {
    const params = new URLSearchParams({ format });
    if (filterUser) params.append("user_id", filterUser);
    if (filterModule) params.append("module", filterModule);
    if (filterSeverity) params.append("severity", filterSeverity);
    if (filterDateFrom) params.append("date_from", filterDateFrom);
    if (filterDateTo) params.append("date_to", filterDateTo);
    if (searchText) params.append("search", searchText);

    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/audit-log/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "excel" ? "Audit_Log.xlsx" : "Audit_Log.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format === "excel" ? "Excel" : "PDF"} exportiert`);
    } else {
      toast.error("Export fehlgeschlagen");
    }
  };

  const resetFilters = () => {
    setFilterUser(""); setFilterModule(""); setFilterSeverity("");
    setFilterDateFrom(""); setFilterDateTo(""); setSearchText("");
    setPage(1);
  };

  // User Detail View
  if (selectedUserId) {
    return (
      <div className="p-6 max-w-5xl mx-auto" data-testid="user-detail-view">
        <button onClick={() => setSelectedUserId(null)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4">
          <ChevronLeft className="w-4 h-4" />Zurueck zur Historie
        </button>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{userInfo?.name || "Mitarbeiter"}</h2>
            <p className="text-sm text-gray-500">Tagesverlauf - {userDate}</p>
          </div>
          <Input type="date" value={userDate} onChange={e => { setUserDate(e.target.value); loadUserDetail(selectedUserId, e.target.value); }}
            className="w-44" data-testid="user-detail-date" />
        </div>

        {userEntries.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><Activity className="w-8 h-8 mx-auto mb-2 opacity-30" /><p>Keine Aktivitaeten an diesem Tag</p></div>
        ) : (
          <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
            {userEntries.map((e, i) => {
              const mod = MODULE_CONFIG[e.module] || MODULE_CONFIG.system;
              const sev = SEVERITY_CONFIG[e.severity] || SEVERITY_CONFIG.ok;
              const ModIcon = mod.icon;
              return (
                <div key={i} className={`relative ${sev.bg} ${sev.border} border rounded-xl p-4`} data-testid={`user-event-${i}`}>
                  <div className={`absolute -left-[calc(1.5rem+5px)] top-5 w-3 h-3 rounded-full ${sev.color === "text-green-600" ? "bg-green-500" : sev.color === "text-amber-600" ? "bg-amber-500" : "bg-red-500"} border-2 border-white`} />
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-lg ${mod.bg}`}><ModIcon className={`w-3.5 h-3.5 ${mod.color}`} /></div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{e.action}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{e.description}</p>
                        {e.project_name && <p className="text-[10px] text-gray-400 mt-1">Projekt: {e.project_name}</p>}
                      </div>
                    </div>
                    <span className="text-xs font-mono text-gray-500 flex-shrink-0">{e.time}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Main View
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-500" />System Historie
          </h1>
          <p className="text-sm text-gray-500 mt-1">{total} Eintraege gesamt</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportLog("pdf")} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="export-pdf-btn">
            <FileText className="w-4 h-4" />PDF
          </button>
          <button onClick={() => exportLog("excel")} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4" />Excel
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Heute" value={stats.total} icon={Activity} color="bg-blue-50 text-blue-500" />
          <StatCard label="Kritisch" value={stats.critical} icon={AlertOctagon} color="bg-red-50 text-red-500" />
          <StatCard label="Warnungen" value={stats.warnings} icon={AlertTriangle} color="bg-amber-50 text-amber-500" />
          <StatCard label="Aktive Nutzer" value={stats.active_users} icon={Users} color="bg-green-50 text-green-500" />
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }}
                placeholder="Freitext-Suche..." className="pl-9" data-testid="audit-search" />
            </div>
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border ${showFilters ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-white border-gray-200 text-gray-600"}`}
            data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4" />Filter
          </button>
          {(filterUser || filterModule || filterSeverity || filterDateFrom || filterDateTo) && (
            <button onClick={resetFilters} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg" data-testid="reset-filters-btn">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-gray-100" data-testid="filter-panel">
            <div>
              <Label className="text-xs text-gray-500">Mitarbeiter</Label>
              <select value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }}
                className="w-full mt-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5" data-testid="filter-user">
                <option value="">Alle</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Modul</Label>
              <select value={filterModule} onChange={e => { setFilterModule(e.target.value); setPage(1); }}
                className="w-full mt-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5" data-testid="filter-module">
                <option value="">Alle</option>
                {Object.entries(MODULE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Schwere</Label>
              <select value={filterSeverity} onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}
                className="w-full mt-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5" data-testid="filter-severity">
                <option value="">Alle</option>
                {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Von</Label>
              <Input type="date" value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }}
                className="mt-1 text-sm" data-testid="filter-date-from" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Bis</Label>
              <Input type="date" value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1); }}
                className="mt-1 text-sm" data-testid="filter-date-to" />
            </div>
          </div>
        )}
      </div>

      {/* Entries List */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-gray-400"><Activity className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><Shield className="w-8 h-8 mx-auto mb-2 opacity-30" /><p>Keine Eintraege</p></div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map((e, i) => {
              const mod = MODULE_CONFIG[e.module] || MODULE_CONFIG.system;
              const sev = SEVERITY_CONFIG[e.severity] || SEVERITY_CONFIG.ok;
              const ModIcon = mod.icon;
              const SevIcon = sev.icon;
              return (
                <div key={i} className={`px-4 py-3 hover:bg-gray-50 transition-colors ${sev.bg}`} data-testid={`audit-entry-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${mod.bg}`}>
                      <ModIcon className={`w-3.5 h-3.5 ${mod.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{e.action}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${mod.bg} ${mod.color}`}>{mod.label}</span>
                        <SevIcon className={`w-3 h-3 ${sev.color}`} />
                      </div>
                      <p className="text-xs text-gray-600 truncate">{e.description}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button onClick={() => openUserDetail(e.user_id)} className="text-xs text-indigo-600 hover:underline" data-testid={`user-link-${i}`}>
                        {e.user_name}
                      </button>
                      <span className="text-xs font-mono text-gray-400">{e.date} {e.time}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-500">Seite {page} von {pages} ({total} Eintraege)</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
                className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" data-testid="page-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
